import React, { memo, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./SimpleExcelViewer.css";
import { toColumnLabel } from "./utils/columnUtils";
import { withLiveChartData } from "./utils/chartLiveData";

import FormulaBar from "./components/FormulaBar";
import Grid from "./components/Grid";
import SheetTabs from "./components/SheetTabs";
import { HeaderContextMenu, FreezeContextMenu } from "./components/ContextMenus";
import CustomSortDialog from "./components/CustomSortDialog/CustomSortDialog";

import useWorkbookLoader from "./hooks/useWorkbookLoader";
import useSheetModel from "./hooks/useSheetModel";
import useCellSelection from "./hooks/useCellSelection";
import useFilters from "./hooks/useFilters";
import useHiddenRowsCols from "./hooks/useHiddenRowsCols";
import useResize from "./hooks/useResize";
import useFreezePanes from "./hooks/useFreezePanes";
import useScrollMemory from "./hooks/useScrollMemory";
import useRowWindow from "./hooks/useRowWindow";
import useColumnGrowth from "./hooks/useColumnGrowth";
import useRowGrowth from "./hooks/useRowGrowth";
import useSort from "./hooks/useSort";
import useMerge from "./hooks/useMerge";
import useKeyboardNav from "./hooks/useKeyboardNav";
import useClipboard from "./hooks/useClipboard";
import useHyperlinks from "./hooks/useHyperlinks";
import useHeaderContextMenu from "./hooks/useHeaderContextMenu";
import useZoom from "./hooks/useZoom";
import usePrint from "./hooks/usePrint";
import useCellEditing from "./hooks/useCellEditing";
import InCellEditor from "./components/Overlays/InCellEditor";
import CellHistoryDialog from "./components/CellHistoryDialog/CellHistoryDialog";
import AllChangesDialog from "./components/CellHistoryDialog/AllChangesDialog";

/**
 * SimpleExcelViewer — orchestrator.
 *
 * This component is intentionally thin: every piece of behaviour lives
 * in its own custom hook under ./hooks/* and every piece of UI in
 * ./components/*.  This file just wires them together.
 */
const SimpleExcelViewer = forwardRef(({
  documentId, pageNumber, openId, userId,
  fileId,                       // back-compat alias for documentId
  excelPayload,                 // { excelData (base64), fileType, fileName } from DocumentRPD
  height = "calc(87vh - 62px)",
  zoom = 1,
  zoomScale: zoomScaleProp,
  onLocked,
  onSaveSuccess,                // bubble save-success up to DocumentRPD's alert
  onDirtyChange,                // (bool) => void — unsaved-edit state for nav guards
}, ref) => {
  const effectiveDocumentId = documentId || fileId;
  const componentRef = useRef(null);

  // ---- Workbook loading & sheet model ----
  // NOTE: The hook no longer issues its own /images/ request.  It consumes
  // the payload supplied by DocumentRPD's single fetch.
  const {
    loading, error, sheetNames, setSheetNames, sheetDataByName, setSheetDataByName,
    workbookBufferRef,
  } = useWorkbookLoader({ excelPayload, onLocked });

  const {
    activeSheet, setActiveSheet, prevSheetRef,
    activeModel, maxCols, colWidths,
    sheetImages, sheetCharts,
    // Raw (unsorted) fields — useSort produces the display versions below.
    normalizedRows: rawRows, rowHeights: rawHeights,
    cellStyles: rawStyles, cellRichHtml: rawRich,
    cellMerges: rawMerges, cellLinks: rawLinks,
  } = useSheetModel({ sheetNames, sheetDataByName });

  // ---- Column sorting (produces the display row data + keyed maps) ----
  const {
    sortLevels, getSortState, sortAsc, sortDesc, clearSort, applySort,
    hasHeaderRow,
    normalizedRows, rowHeights, cellStyles, cellRichHtml, cellLinks, cellMerges, formulas: displayFormulas,
  } = useSort({
    activeSheet, prevSheetRef, activeModel,
    rawRows, rawHeights, rawStyles, rawRich, rawLinks, rawMerges,
  });

  const [customSortOpen, setCustomSortOpen] = useState(false);
  // { row, col } of the cell whose edit history is being viewed.
  const [historyTarget, setHistoryTarget] = useState(null);
  // Whole-workbook "All changes" dialog.
  const [allHistoryOpen, setAllHistoryOpen] = useState(false);


  // ---- Selection / filtering / hidden rows-cols ----
  const {
    selectedCell, setSelectedCell, setSelection,
    selectionType, setSelectionType, normalizedSelection,
    startSelection, extendSelection, selectColumn, selectRow,
    extendColumnSelection, extendRowSelection,
  } = useCellSelection({ activeSheet, normalizedRows, maxCols });

  // ---- Cell merging ----
  // The interactive Merge & Center toolbar is intentionally hidden, so the
  // merge handlers below are not consumed; useMerge now simply passes the
  // FILE's own merged cells through unchanged so they still render as-is.
  const {
    cellMerges: finalMerges, cellStyles: finalStyles,
  } = useMerge({
    activeSheet,
    baseMerges: cellMerges,
    baseStyles: cellStyles,
    selection: normalizedSelection,
  });

  const {
    hiddenCols, hiddenRows,
    handleHideCol, handleUnhideCols, handleHideRow, handleUnhideRows,
    isLastVisibleBeforeHidden, isLastVisibleBeforeHiddenRow,
  } = useHiddenRowsCols({ activeSheet, prevSheetRef });

  const {
    filters, activeFilterCol, setActiveFilterCol,
    toggleFilterMenu, applyFilter, isRowVisible,
    filterEnabled, isFilterColumn, toggleFilterEnabled,
  } = useFilters({
    activeSheet, activeModel, normalizedRows, hiddenRows,
    // Column selections scope the Filter toggle to just those columns.
    selectionType, normalizedSelection,
    // Signature of the current sort order — the filter row-snapshot is
    // recomputed when rows are reordered (but NOT on cell edits).
    sortSignature: JSON.stringify(sortLevels),
  });

  // Rename a sheet everywhere the display model knows it (names list, data
  // map, active tab). Persisting into the saved file happens in
  // useCellEditing via registerSheetRename.
  const applySheetRename = useCallback((oldName, newName) => {
    setSheetNames((prev) => prev.map((n) => (n === oldName ? newName : n)));
    setSheetDataByName((prev) => {
      if (!prev[oldName]) return prev;
      const next = {};
      // Preserve tab order while swapping the key.
      Object.keys(prev).forEach((k) => { next[k === oldName ? newName : k] = prev[k]; });
      return next;
    });
    setActiveSheet((prev) => (prev === oldName ? newName : prev));
  }, [setSheetNames, setSheetDataByName, setActiveSheet]);

  // ---- In-place cell editing (opt-in per reader email via AppContext) ----
  const {
    canEdit: editingAllowed, canSave: editingCanSave, editMode, toggleEditMode,
    commitCell, commitCells, registerSheetRename,
    save: saveEdits, dirty: editsDirty,
    saving: editsSaving, statusMsg: editStatusMsg,
    historyEnabled, fetchHistory,
    cancelEdits, saveSuccess, clearSaveSuccess,
  } = useCellEditing({
    userId,
    documentId: effectiveDocumentId,
    openId,
    fileName: excelPayload?.fileName,
    fileType: excelPayload?.fileType,
    activeSheet, sheetDataByName, setSheetDataByName, workbookBufferRef,
    sortActive: sortLevels.length > 0,
    filterEnabled,
    applySheetRename,
  });

  // Sheet rename entry point (SheetTabs double-click). Renaming counts as an
  // edit: entering it outside edit mode switches edit mode ON (clearing any
  // sort/filter first, same as the Edit button) so Save/Cancel appear.
  const handleSheetRename = useCallback((oldName, newName) => {
    const next = String(newName || "").trim();
    if (!next || next === oldName || sheetNames.includes(next)) return;
    if (!editMode) {
      if (sortLevels.length > 0) clearSort();
      if (filterEnabled) toggleFilterEnabled();
      toggleEditMode();
    }
    applySheetRename(oldName, next);
    registerSheetRename(oldName, next);
  }, [sheetNames, applySheetRename, registerSheetRename,
      editMode, sortLevels.length, clearSort, filterEnabled, toggleFilterEnabled, toggleEditMode]);

  // Surface the unsaved-edit state to the parent (page-navigation guard) and
  // warn on tab close while edits are pending.
  useEffect(() => {
    if (typeof onDirtyChange === "function") onDirtyChange(!!editsDirty);
  }, [editsDirty, onDirtyChange]);
  useEffect(() => {
    if (!editsDirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [editsDirty]);
  // Bubble a successful save up to DocumentRPD so its top-level Alert shows
  // the toast on the MAIN page (not clipped inside the viewer). Clear the
  // local flag immediately - the parent owns the visible alert now.
  useEffect(() => {
    if (saveSuccess) {
      if (typeof onSaveSuccess === "function") onSaveSuccess("Data saved successfully");
      clearSaveSuccess();
    }
  }, [saveSuccess, onSaveSuccess, clearSaveSuccess]);


  // Column options for the Custom Sort dialog — both the letter and the
  // first-row header value, so the dialog can label by whichever the
  // "My data has headers" checkbox selects.
  const sortColumns = useMemo(() => {
    const n = activeModel?.dataColCount || maxCols;
    const header = normalizedRows[0];
    return Array.from({ length: n }, (_, c) => {
      const h = header && header[c] != null && header[c] !== "" ? String(header[c]) : null;
      return { value: c, letter: toColumnLabel(c), header: h };
    });
  }, [activeModel?.dataColCount, maxCols, normalizedRows]);

  // ---- Resize + zoom ----
  const { startResize } = useResize({ activeSheet, colWidths, rowHeights, setSheetDataByName });
  const zoomScale = useZoom(zoomScaleProp, zoom);

  // ---- Freeze panes ----
  const {
    frozenRows, frozenCols, handleFreeze, handleUnfreeze,
    freezeCtx, setFreezeCtx, handleCellContextMenu,
    gridWrapperRef, theadRef,
    measuredTheadHeight, frozenRowOffsets, frozenColOffsets,
  } = useFreezePanes({
    activeSheet, sheetDataByName,
    rowHeights, colWidths,
    hiddenRows, hiddenCols, isRowVisible,
    normalizedRowsLength: normalizedRows.length,
    zoomScale,
  });

  // ---- Endless grid: grow rendered columns/rows on scroll (like Excel) ----
  const displayCols = useColumnGrowth({ gridWrapperRef, baseCols: maxCols, colWidths, activeSheet });
  const displayRows = useRowGrowth({ gridWrapperRef, baseRows: normalizedRows.length, rowHeights, activeSheet });

  // ---- Scroll memory + keyboard nav + hyperlinks ----
  const { pendingLinkScrollRef } = useScrollMemory({ gridWrapperRef, activeSheet });

  // ---- Row virtualization (only the visible slice is mounted) ----
  const rowWindow = useRowWindow({
    gridWrapperRef,
    rowCount: displayRows,
    rowHeights,
    isRowVisible,
    frozenRows,
    cellMerges: finalMerges,
    activeSheet,
  });

  useKeyboardNav({
    activeModel, normalizedRows, maxCols: displayCols, colWidths, rowHeights,
    gridWrapperRef, selectedCell, setSelectedCell, setSelection, setSelectionType,
  });

  // ---- Copy / paste (Ctrl+C anywhere, Ctrl+V while editing) ----
  // Gated on the ExcelWebEdit capability: RPDs are copy-protected documents,
  // so readers WITHOUT edit rights keep the document-protection behaviour
  // (no cell copying) instead of getting a new exfiltration path.
  useClipboard({
    enabled: editingAllowed,
    editMode,
    selectedCell,
    normalizedSelection,
    normalizedRows,
    commitCells,
  });

  // ---- Charts re-resolved against LIVE sheet data ----
  // Parsed charts carry the file's cached series values; after an edit the
  // cache is stale. Re-resolve each series' source range against the current
  // model so graphs mirror the reader's changes.
  const liveCharts = useMemo(
    () => withLiveChartData(sheetCharts, sheetDataByName),
    [sheetCharts, sheetDataByName]
  );

  const { handleLinkClick } = useHyperlinks({
    sheetNames, activeSheet, setActiveSheet, sheetDataByName,
    colWidths, rowHeights, gridWrapperRef,
    setSelectedCell, setSelection, pendingLinkScrollRef,
  });

  // ---- Header context menu ----
  const { headerCtx, handleHeaderContextMenu, closeHeaderContextMenu } = useHeaderContextMenu();

  // Reset header context menu when active sheet changes.
  useEffect(() => { closeHeaderContextMenu(); }, [activeSheet, closeHeaderContextMenu]);

  // ---- Imperative print API ----
  usePrint({ ref, workbookBufferRef, componentRef });

  // ---- Derived UI strings (formula bar) ----
  const selectionSummary = useMemo(() => {
    if (selectionType === "col") {
      const c1 = normalizedSelection.c1;
      const c2 = normalizedSelection.c2;
      return c1 === c2 ? `${toColumnLabel(c1)}:${toColumnLabel(c1)}` : `${toColumnLabel(c1)}:${toColumnLabel(c2)}`;
    }
    if (selectionType === "row") {
      const r1 = normalizedSelection.r1 + 1;
      const r2 = normalizedSelection.r2 + 1;
      return r1 === r2 ? `${r1}:${r1}` : `${r1}:${r2}`;
    }
    const rowsCount = normalizedSelection.r2 - normalizedSelection.r1 + 1;
    const colsCount = normalizedSelection.c2 - normalizedSelection.c1 + 1;
    return rowsCount === 1 && colsCount === 1 ? "" : `${rowsCount}R x ${colsCount}C`;
  }, [normalizedSelection, selectionType]);

  const selectedCellLabel = useMemo(() => {
    if (selectionType === "col") {
      const c1 = normalizedSelection.c1;
      const c2 = normalizedSelection.c2;
      return c1 === c2 ? `${toColumnLabel(c1)}:${toColumnLabel(c1)}` : `${toColumnLabel(c1)}:${toColumnLabel(c2)}`;
    }
    if (selectionType === "row") {
      const r1 = normalizedSelection.r1 + 1;
      const r2 = normalizedSelection.r2 + 1;
      return r1 === r2 ? `${r1}:${r1}` : `${r1}:${r2}`;
    }
    return `${toColumnLabel(selectedCell.col)}${selectedCell.row + 1}`;
  }, [selectedCell.col, selectedCell.row, selectionType, normalizedSelection]);

  const selectedCellText = useMemo(() => {
    if (!activeModel) return "";
    const formula = displayFormulas?.[`${selectedCell.row}:${selectedCell.col}`];
    if (formula) return formula;
    const value = normalizedRows?.[selectedCell.row]?.[selectedCell.col];
    return value == null ? "" : String(value);
  }, [activeModel, displayFormulas, normalizedRows, selectedCell.col, selectedCell.row]);

  // ---- Early returns for loading / error / empty ----
  if (loading) return <div className="simple-excel-viewer-status" style={{ height }}>Loading Excel...</div>;
  if (error)   return <div className="simple-excel-viewer-status simple-excel-viewer-error" style={{ height }}>{error}</div>;
  if (sheetNames.length === 0) return <div className="simple-excel-viewer-status" style={{ height }}>No sheets found in this file.</div>;

  return (
    <div className="simple-excel-viewer" style={{ height }}>
      <FormulaBar
        selectedCellLabel={selectedCellLabel}
        selectionSummary={selectionSummary}
        selectedCellText={selectedCellText}
        filterEnabled={filterEnabled}
        onToggleFilter={toggleFilterEnabled}
        onCustomSort={() => setCustomSortOpen(true)}
        sortActive={sortLevels.length > 0}
        editing={editingAllowed ? {
          editMode,
          // Never disable Edit for sort/filter - instead entering edit mode
          // clears them (below) so display coords match the file, keeping
          // edits safe. editBlocked stays false so the icon is clickable.
          editBlocked: false,
          dirty: editsDirty,
          saving: editsSaving,
          statusMsg: editStatusMsg,
          canSave: editingCanSave,
          onToggleEdit: () => {
            if (!editMode) {
              // Clear any active sort/filter so the edit grid is 1:1 with
              // the underlying file rows before editing begins.
              if (sortLevels.length > 0) clearSort();
              if (filterEnabled) toggleFilterEnabled();
            }
            toggleEditMode();
          },
          onCancel: cancelEdits,
          onSave: saveEdits,
        } : null}
        onCommitAt={commitCell}
        selectedRow={selectedCell.row}
        selectedCol={selectedCell.col}
        editTargetKey={`${activeSheet}|${selectedCell.row}|${selectedCell.col}`}
        onShowAllHistory={historyEnabled ? () => setAllHistoryOpen(true) : undefined}
      />

      <Grid
        wrapperRef={gridWrapperRef}
        theadRef={theadRef}
        tableRef={componentRef}
        zoomScale={zoomScale}
        maxCols={displayCols}
        colWidths={colWidths}
        hiddenCols={hiddenCols}
        hiddenRows={hiddenRows}
        frozenCols={frozenCols}
        frozenColOffsets={frozenColOffsets}
        selectionType={selectionType}
        normalizedSelection={normalizedSelection}
        filters={filters}
        activeFilterCol={activeFilterCol}
        normalizedRows={normalizedRows}
        onToggleFilter={toggleFilterMenu}
        onApplyFilter={applyFilter}
        onCloseFilter={() => setActiveFilterCol(null)}
        onSelectColumn={selectColumn}
        onExtendColumn={extendColumnSelection}
        onHeaderContextMenu={handleHeaderContextMenu}
        onStartResize={startResize}
        isLastVisibleBeforeHidden={isLastVisibleBeforeHidden}
        isLastVisibleBeforeHiddenRow={isLastVisibleBeforeHiddenRow}
        rowHeights={rowHeights}
        cellStyles={finalStyles}
        cellRichHtml={cellRichHtml}
        cellMerges={finalMerges}
        cellLinks={cellLinks}
        isRowVisible={isRowVisible}
        selectedCell={selectedCell}
        frozenRows={frozenRows}
        measuredTheadHeight={measuredTheadHeight}
        frozenRowOffsets={frozenRowOffsets}
        onSelectRow={selectRow}
        onExtendRow={extendRowSelection}
        onStartSelection={startSelection}
        onExtendSelection={extendSelection}
        onLinkClick={handleLinkClick}
        onCellContextMenu={handleCellContextMenu}
        activeSheet={activeSheet}
        sheetImages={sheetImages}
        sheetCharts={liveCharts}
        rowWindow={rowWindow}
        getSortState={getSortState}
        isFilterColumn={isFilterColumn}
        onSortAsc={sortAsc}
        onSortDesc={sortDesc}
        onClearSort={clearSort}
        onCustomSort={() => setCustomSortOpen(true)}
      />

      <InCellEditor
        active={editMode}
        gridWrapperRef={gridWrapperRef}
        selectedCell={selectedCell}
        rows={normalizedRows}
        formulas={displayFormulas}
        onCommit={commitCell}
        onNavigate={(dRow, dCol) => {
          // Move the selection like Excel does after a commit.
          const row = Math.max(0, Math.min(normalizedRows.length - 1, selectedCell.row + dRow));
          const col = Math.max(0, Math.min(maxCols - 1, selectedCell.col + dCol));
          const cell = { row, col };
          setSelectedCell(cell);
          setSelection({ anchor: cell, focus: cell, dragging: false });
        }}
      />

      <SheetTabs
        sheetNames={sheetNames}
        activeSheet={activeSheet}
        onSelect={setActiveSheet}
        canRename={!!editingAllowed}
        onRename={handleSheetRename}
      />

      {headerCtx && (
        <HeaderContextMenu
          type={headerCtx.type}
          index={headerCtx.index}
          position={{ x: headerCtx.x, y: headerCtx.y }}
          hiddenSet={headerCtx.type === "col" ? hiddenCols : hiddenRows}
          onHide={headerCtx.type === "col" ? handleHideCol : handleHideRow}
          onUnhide={headerCtx.type === "col" ? handleUnhideCols : handleUnhideRows}
          onClose={closeHeaderContextMenu}
        />
      )}

      <FreezeContextMenu
        ctx={freezeCtx}
        frozenRows={frozenRows}
        frozenCols={frozenCols}
        onFreeze={handleFreeze}
        onUnfreeze={handleUnfreeze}
        onClose={() => setFreezeCtx(null)}
        onShowHistory={historyEnabled ? (row, col) => {
          setFreezeCtx(null);
          setHistoryTarget({ row, col });
        } : undefined}
      />

      {historyEnabled && (
        <CellHistoryDialog
          target={historyTarget}
          sheet={activeSheet}
          fetchHistory={fetchHistory}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {historyEnabled && (
        <AllChangesDialog
          open={allHistoryOpen}
          fetchHistory={fetchHistory}
          onClose={() => setAllHistoryOpen(false)}
        />
      )}


      <CustomSortDialog
        open={customSortOpen}
        columns={sortColumns}
        initialLevels={sortLevels}
        initialHasHeaderRow={hasHeaderRow}
        onApply={(levels, header) => applySort(levels, header)}
        onClose={() => setCustomSortOpen(false)}
      />
    </div>
  );
});

export default memo(SimpleExcelViewer);
